import React, { useCallback, useEffect, useRef, useState } from "react"
import { makeStyles } from "tss-react/mui"
import {
  Typography,
  Select,
  MenuItem,
  Switch,
  Slider,
} from "@mui/material"
import {
  GraphicEq as GraphicEqIcon,
  Compress as CompressIcon,
  FilterAlt as FilterAltIcon,
  VolumeUp as VolumeUpIcon,
} from "@mui/icons-material"
import voiceChain, { createPreviewChain, createSelfListenMonitor } from "../services/voiceChain"
import MicLevelMeter from "./MicLevelMeter"

/**
 * "Microphone processing" controls for Settings → Voice & Video — the
 * user-facing face of the publisher vocal chain (services/voiceChain.js).
 *
 * Metering:
 *  - While PUBLISHING, the meter reads the live publish chain — what watchers
 *    actually receive.
 *  - While NOT publishing, we open a temporary mic-only capture, run it
 *    through a throwaway preview chain (never connected to the speakers — no
 *    feedback), and meter that, so the chain can be tuned before going live.
 *    The capture is released the moment the panel unmounts.
 *
 * All controls commit immediately via onChange(partial) — the parent persists
 * and live-applies them (no OK button, same convention as DeviceSettingsPanel).
 * EC/NS toggles re-acquire the mic (capture constraints), which the parent
 * handles; everything else applies in place with no re-publish.
 *
 * Props:
 *  - settings  : the micProcessing prefs object (see DEFAULT_MIC_PROCESSING)
 *  - onChange(partial)
 *  - isPublishing
 *  - savedAudioDeviceId : mic to use for the preview capture ('' = default)
 */
const useStyles = makeStyles()((theme) => ({
  row: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
    maxWidth: 420,
  },
  rowIcon: {
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  rowLabel: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },
  rowTitle: {
    fontSize: "0.9rem",
    color: theme.palette.text.primary,
  },
  rowSub: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  sliderBox: {
    flex: 1,
    minWidth: 0,
    padding: theme.spacing(0, 1),
  },
  select: {
    minWidth: 150,
  },
  meterRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    maxWidth: 420,
    marginBottom: theme.spacing(1),
  },
  meterHint: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    maxWidth: 420,
    marginBottom: theme.spacing(1),
  },
  advancedHeading: {
    fontSize: "0.8rem",
    fontWeight: 600,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    margin: theme.spacing(2, 0, 0.5),
  },
  warnText: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    maxWidth: 420,
    marginBottom: theme.spacing(0.5),
  },
}))

const GAIN_MARKS = [
  { value: -24, label: "-24" },
  { value: -12, label: "-12" },
  { value: 0, label: "0 dB" },
  { value: 12, label: "+12" },
  { value: 24, label: "+24" },
]

const MicProcessingPanel = ({
  settings,
  onChange,
  isPublishing = false,
  savedAudioDeviceId = "",
  // Optional: an already-open capture to meter (the device-setup modal's
  // preview). Its audio track is CLONED for the throwaway chain — we never
  // stop or own the caller's track — and no second getUserMedia happens
  // (which would break iOS's single-capture rule).
  previewStream = null,
}) => {
  const { classes } = useStyles()
  const [previewChain, setPreviewChain] = useState(null)
  const [previewError, setPreviewError] = useState(null)
  const [gateAvail, setGateAvail] = useState(voiceChain.gateAvailable)
  // Self-listen monitor chain (see getListenStream below) — declared up here
  // because the settings-sync effect reads it.
  const [listenChain, setListenChain] = useState(null)
  const listenMonitorRef = useRef(null)
  // Latest settings for async preview-chain hydration without re-running it.
  const settingsRef = useRef(settings)
  settingsRef.current = settings

  // The chain whose meter/gate we display.
  const activeChain = isPublishing ? voiceChain : previewChain

  // Preview capture while not publishing. RAW on purpose (all browser DSP
  // off): this capture only feeds the meter and the self-listen monitor —
  // never the room — and any concurrent EC'd capture of the same mic lets
  // the echo canceller eat the monitor playback. The EC/NS toggles are about
  // the PUBLISH capture; they apply on (re)publish.
  const previewAudioTrack = previewStream ? previewStream.getAudioTracks()[0] || null : null
  useEffect(() => {
    if (isPublishing) return undefined
    let cancelled = false
    const chain = createPreviewChain()
    chain.setLimiterEnabled(voiceChain.limiterEnabled)
    ;(async () => {
      let track = null
      if (previewAudioTrack) {
        // Borrow the caller's capture: clone the track so the chain can own
        // (and stop) its copy without touching the original.
        track = previewAudioTrack.clone()
      } else {
        const base = {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        }
        let stream = null
        try {
          try {
            stream = await navigator.mediaDevices.getUserMedia({
              audio: savedAudioDeviceId
                ? { ...base, deviceId: { exact: savedAudioDeviceId } }
                : base,
            })
          } catch (exactErr) {
            // Stale pinned id — fall back to the default mic for the preview.
            stream = await navigator.mediaDevices.getUserMedia({ audio: base })
          }
        } catch (err) {
          if (!cancelled) {
            setPreviewError(
              "Couldn't open your microphone for the level preview — check permissions. Settings still save."
            )
          }
          return
        }
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        track = stream.getAudioTracks()[0]
      }
      if (cancelled) {
        try {
          track && track.stop()
        } catch (e) {
          /* noop */
        }
        return
      }
      // The chain owns the (possibly cloned) raw track from here.
      chain.attachSource(track)
      chain.updateSettings(settingsRef.current)
      setPreviewError(null)
      setPreviewChain(chain)
    })()
    return () => {
      cancelled = true
      chain.dispose() // releases the preview mic / stops the clone
      setPreviewChain(null)
    }
  }, [isPublishing, savedAudioDeviceId, previewAudioTrack])

  // Keep the metered chain(s) in sync with the controls. (For the live chain
  // the parent already applies changes; this is idempotent and also covers
  // the preview + self-listen chains.)
  useEffect(() => {
    if (activeChain) activeChain.updateSettings(settings)
    if (listenChain) listenChain.updateSettings(settings)
  }, [activeChain, listenChain, settings])

  // The gate worklet loads asynchronously — re-check shortly after the chain
  // appears so the control un-disables without a remount.
  useEffect(() => {
    if (!activeChain) return undefined
    setGateAvail(activeChain.gateAvailable)
    const t = setTimeout(() => setGateAvail(activeChain.gateAvailable), 1500)
    return () => clearTimeout(t)
  }, [activeChain])

  // The chain the meter displays: the self-listen monitor takes precedence
  // while it's live (see above), else the publish/preview chain.
  const meterChain = listenChain || activeChain
  const getLevel = useCallback(
    () => (meterChain ? meterChain.getLevel() : { rms: 0, peak: 0 }),
    [meterChain]
  )

  // Self-listen monitor: its own EC-free capture through a throwaway chain
  // (EC on would make the AEC "cancel" your own playback — audible pumping),
  // with the outbound mic paused for its whole lifetime so the test never
  // broadcasts (both handled inside createSelfListenMonitor). While it runs,
  // the meter reads the MONITOR chain — the regular EC'd capture keeps
  // pumping silently in the background and would mislead the bar.
  const getListenStream = useCallback(async () => {
    const monitor = await createSelfListenMonitor({
      audioDeviceId: savedAudioDeviceId,
      settings: settingsRef.current,
      limiterEnabled: voiceChain.limiterEnabled,
      // While LIVE, swap the publish chain's own source to the raw monitor
      // capture instead of opening a parallel one — a concurrent EC'd capture
      // of the same mic lets the echo canceller converge on the monitor
      // playback and cancel sustained notes to zero. Restored (with the
      // user's real EC/NS prefs) when the monitor stops.
      liveChain: isPublishing ? voiceChain : null,
      restoreConstraints: isPublishing
        ? {
            echoCancellation: !settingsRef.current.disableEchoCancellation,
            noiseSuppression: !settingsRef.current.disableNoiseSuppression,
          }
        : null,
    })
    listenMonitorRef.current = monitor
    setListenChain(monitor.chain)
    return monitor.stream
  }, [savedAudioDeviceId, isPublishing])
  const handleListenStop = useCallback(() => {
    if (listenMonitorRef.current) {
      listenMonitorRef.current.dispose() // stops capture + restores outbound mic
      listenMonitorRef.current = null
      setListenChain(null)
    }
  }, [])

  const commit = (partial) => onChange && onChange(partial)

  return (
    <>
      {/* Live level, post-chain — what actually gets encoded. */}
      <div className={classes.meterRow}>
        <GraphicEqIcon className={classes.rowIcon} fontSize="small" />
        <MicLevelMeter
          getLevel={meterChain ? getLevel : null}
          gateSource={meterChain}
          showGate={settings.gate !== "off" && gateAvail}
          getListenStream={getListenStream}
          onListenStop={handleListenStop}
        />
      </div>
      <Typography className={classes.meterHint}>
        {previewError ||
          (isPublishing
            ? "Live level after processing — this is what others hear."
            : "Speak to test — the meter shows your level after processing.")}
      </Typography>

      <div className={classes.row}>
        <VolumeUpIcon className={classes.rowIcon} fontSize="small" />
        <div className={classes.rowLabel}>
          <span className={classes.rowTitle}>Input gain</span>
          <span className={classes.rowSub}>Boost a quiet mic (or tame a hot one)</span>
        </div>
      </div>
      <div className={classes.row}>
        <div className={classes.sliderBox}>
          <Slider
            value={typeof settings.inputGainDb === "number" ? settings.inputGainDb : 0}
            min={-24}
            max={24}
            step={1}
            marks={GAIN_MARKS}
            size="small"
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v > 0 ? "+" : ""}${v} dB`}
            onChange={(e, v) => commit({ inputGainDb: v })}
            aria-label="Microphone input gain"
          />
        </div>
      </div>

      <div className={classes.row}>
        <FilterAltIcon className={classes.rowIcon} fontSize="small" />
        <div className={classes.rowLabel}>
          <span className={classes.rowTitle}>Low-cut filter</span>
          <span className={classes.rowSub}>Removes rumble below 200 Hz (fans, desk bumps)</span>
        </div>
        <Switch
          checked={!!settings.highPass}
          onChange={() => commit({ highPass: !settings.highPass })}
          color="primary"
          slotProps={{ input: { "aria-label": "Low-cut filter" } }}
        />
      </div>

      <div className={classes.row}>
        <CompressIcon className={classes.rowIcon} fontSize="small" />
        <div className={classes.rowLabel}>
          <span className={classes.rowTitle}>Compression</span>
          <span className={classes.rowSub}>Evens out loud and quiet moments</span>
        </div>
        <Select
          className={classes.select}
          variant="outlined"
          margin="dense"
          value={settings.compressor || "off"}
          onChange={(e) => commit({ compressor: e.target.value })}
        >
          <MenuItem value="off">Off</MenuItem>
          <MenuItem value="light">Light</MenuItem>
          <MenuItem value="medium">Medium</MenuItem>
          <MenuItem value="broadcast">Broadcast</MenuItem>
        </Select>
      </div>

      <div className={classes.row}>
        <GraphicEqIcon className={classes.rowIcon} fontSize="small" />
        <div className={classes.rowLabel}>
          <span className={classes.rowTitle}>Noise gate</span>
          <span className={classes.rowSub}>
            {gateAvail
              ? "Quiets keyboard, fans and room noise between sentences"
              : "Not supported by this browser"}
          </span>
        </div>
        <Select
          className={classes.select}
          variant="outlined"
          margin="dense"
          value={settings.gate || "off"}
          disabled={!gateAvail}
          onChange={(e) => commit({ gate: e.target.value })}
        >
          <MenuItem value="off">Off</MenuItem>
          <MenuItem value="light">Light</MenuItem>
          <MenuItem value="medium">Medium</MenuItem>
          <MenuItem value="strong">Strong</MenuItem>
        </Select>
      </div>

      <Typography className={classes.advancedHeading}>Advanced</Typography>

      {settings.gate !== "off" && gateAvail && (
        <>
          <div className={classes.row}>
            <div className={classes.rowLabel}>
              <span className={classes.rowTitle}>Gate reduction</span>
              <span className={classes.rowSub}>
                How much the gate quiets you between sentences
              </span>
            </div>
          </div>
          <div className={classes.row}>
            <div className={classes.sliderBox}>
              <Slider
                value={typeof settings.gateFloorDb === "number" ? settings.gateFloorDb : -15}
                min={-40}
                max={-6}
                step={1}
                size="small"
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `${v} dB`}
                onChange={(e, v) => commit({ gateFloorDb: v })}
                aria-label="Gate reduction depth"
              />
            </div>
          </div>
        </>
      )}

      <Typography className={classes.warnText}>
        For studio mics or headphone setups. Turning these off can cause echo
        for others{isPublishing ? " — your mic restarts briefly when changed" : ""}.
      </Typography>
      <div className={classes.row}>
        <div className={classes.rowLabel}>
          <span className={classes.rowTitle}>Browser echo cancellation</span>
        </div>
        <Switch
          checked={!settings.disableEchoCancellation}
          onChange={() => commit({ disableEchoCancellation: !settings.disableEchoCancellation })}
          color="primary"
          slotProps={{ input: { "aria-label": "Browser echo cancellation" } }}
        />
      </div>
      <div className={classes.row}>
        <div className={classes.rowLabel}>
          <span className={classes.rowTitle}>Browser noise suppression</span>
        </div>
        <Switch
          checked={!settings.disableNoiseSuppression}
          onChange={() => commit({ disableNoiseSuppression: !settings.disableNoiseSuppression })}
          color="primary"
          slotProps={{ input: { "aria-label": "Browser noise suppression" } }}
        />
      </div>
    </>
  )
}

export default MicProcessingPanel
